/**
 * Shared FieldDefinition catalog fetcher. Owns the bits that have to behave
 * identically across every catalog-consuming hook (cancellation token,
 * value-based digest of `queryParams` to defeat fresh-but-equal references,
 * state reset when `enabled` flips off mid-flight, error reporting). Named
 * hooks like `useConditionFieldRegistry` (assignments) and
 * `useRuleSetConditionFieldRegistry` (rules_engine framework) delegate here and
 * just pre-fill `url` and `queryParams`.
 *
 * `queryParams` is encoded as a URL-encoded query string: array values become
 * repeated `key[]=...` entries, scalars become `key=...`. The fetcher does not
 * interpret any param semantically — it's a thin axios wrapper with the
 * race/digest plumbing the call sites kept reimplementing.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';

import { reportAxiosError } from '~/Utils';

import type { FieldDefinitionDto } from './types';

type QueryParamValue = string | number | boolean | readonly (string | number)[];

export interface UseConditionFieldsFetcherParams {
  url: string;
  /** Caller-owned prerequisite gate. When false the hook resets state and
   *  fires no request — `queryParams` may contain placeholder/empty values. */
  enabled: boolean;
  queryParams: Record<string, QueryParamValue>;
}

export interface UseConditionFieldsFetcherResult {
  fields: FieldDefinitionDto[];
  isLoading: boolean;
  isError: boolean;
}

export const useConditionFieldsFetcher = ({
  url,
  enabled,
  queryParams,
}: UseConditionFieldsFetcherParams): UseConditionFieldsFetcherResult => {
  const [fields, setFields] = useState<FieldDefinitionDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Value-digest of queryParams so the effect only re-runs when contents
  // change. Formik hands back fresh array/object references on unrelated
  // keystrokes; depending on `queryParams` directly would refetch the catalog
  // on every form edit. JSON.stringify handles nested arrays — array order
  // matters (the wire format is `key[]=v1&key[]=v2`), which is what we want.
  const queryParamsKey = JSON.stringify(queryParams);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setFields([]);
      // Reset isLoading too: if a previous run was still in flight when the
      // user cleared a prerequisite, its `finally` block skips
      // setIsLoading(false) (cancelled=true), so without this the spinner
      // would stick.
      setIsLoading(false);
      setIsError(false);
      return undefined;
    }
    (async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(queryParams)) {
          if (Array.isArray(value)) {
            for (const item of value) {
              params.append(`${key}[]`, String(item));
            }
          } else {
            params.set(key, String(value));
          }
        }
        const { data } = await axios.get<FieldDefinitionDto[]>(`${url}?${params.toString()}`);
        if (!cancelled) {
          setFields(data);
        }
      } catch (error) {
        if (!cancelled) {
          setIsError(true);
        }
        await reportAxiosError(error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // queryParamsKey is a value-digest of queryParams — see comment above its declaration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled, queryParamsKey]);

  return { fields, isLoading, isError };
};
